import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import ProductSelect from '../components/products/ProductSelect';
import QuickRegistration from '../components/customer/QuickRegistration';
import QuickInvoiceModal from '../components/invoice/QuickInvoiceModal';
import TaxIntel from '../components/shared/TaxIntel';
import { useToast } from '../components/shared/ToastNotification';

// Premium Order Form - Sophisticated & Responsive
function OrderForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        customer_id: '',
        order_date: new Date().toISOString().split('T')[0],
        status: 'confirmed',
        order_type: 'online',
        notes: '',
        discount_amount: 0
    });

    interface Payment {
        id?: number | string;
        amount: number | string;
        payment_method: string;
        transaction_id: string;
        cheque_number: string;
        _destroy?: boolean;
    }

    const [payments, setPayments] = useState<Payment[]>([{ amount: 0, payment_method: 'cash', transaction_id: '', cheque_number: '', _destroy: false }]);
    const [paymentMode, setPaymentMode] = useState('cash');

    interface LineItem {
        id: string | number;
        product_id: string;
        product_unit_id: string | null;
        stock_batch_id: string | null;
        quantity: number;
        unit_price: number;
        discount_percent: number;
        tax_rate_id?: string | number;
        cgst_rate?: number;
        sgst_rate?: number;
        igst_rate?: number;
        tax_type: 'auto' | 'cgst_sgst' | 'igst';
        mrp: number;
        _destroy: boolean;
        availableUnits?: any[];
    }

    const [customer, setCustomer] = useState<any>(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerResults, setCustomerResults] = useState<any[]>([]);
    const [showCustomerResults, setShowCustomerResults] = useState(false);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

    const [lineItems, setLineItems] = useState<LineItem[]>([createEmptyLineItem()]);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errors, setErrors] = useState<string[]>([]);
    const [showQuickInvoice, setShowQuickInvoice] = useState(false);
    const [createdOrder, setCreatedOrder] = useState<any>(null);
    const [taxRates, setTaxRates] = useState<any[]>([]);

    useEffect(() => {
        const init = async () => {
            try {
                setInitialLoading(true);
                await Promise.all([fetchProducts(), fetchTaxRates()]);
                if (isEdit) {
                    await fetchOrder();
                }
            } catch (error) {
                console.error('Error initializing OrderForm:', error);
                setErrors(['Failed to load form data. Please refresh the page.']);
            } finally {
                setInitialLoading(false);
            }
        };
        init();
    }, [id]);

    const fetchProducts = async () => {
        try {
            const response = await api.get('/products?available=true&per_page=1000');
            const productsData = Array.isArray(response.data) ? response.data : (response.data?.products || response.data?.data || []);
            setProducts(productsData);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };
    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const fetchOrder = async () => {
        try {
            const response = await api.get(`/orders/${id}`);
            const order = response.data.data || response.data;
            setFormData({
                customer_id: order.customer_id,
                order_date: order.order_date,
                status: order.status,
                order_type: order.order_type || 'online',
                notes: order.notes || '',
                discount_amount: order.discount_amount || 0
            });
            setCustomer(order.customer);

            if (order.payments && order.payments.length > 0) {
                setPayments(order.payments.map((p: any) => ({
                    id: p.id,
                    amount: p.amount,
                    payment_method: p.payment_method || 'cash',
                    transaction_id: p.transaction_id || '',
                    cheque_number: p.cheque_number || '',
                    _destroy: false
                })));
                if (order.payments.length > 1) {
                    setPaymentMode('split');
                } else {
                    setPaymentMode(order.payments[0].payment_method || 'cash');
                }
            }
            setCustomerSearch(order.customer?.name || '');
            if (order.items && order.items.length > 0) {
                setLineItems(order.items.map((item: any) => ({
                    id: item.id,
                    product_id: item.product_id,
                    product_unit_id: item.product_unit_id,
                    stock_batch_id: item.stock_batch_id || null,
                    quantity: parseFloat(item.quantity),
                    unit_price: parseFloat(item.unit_price),
                    discount_percent: parseFloat(item.discount_percent || 0),
                    tax_rate_id: item.tax_rate_id,
                    tax_rate: parseFloat(item.tax_rate || 0),
                    cgst_rate: parseFloat(item.cgst_rate || 0),
                    sgst_rate: parseFloat(item.sgst_rate || 0),
                    igst_rate: parseFloat(item.igst_rate || 0),
                    tax_type: item.tax_type || 'auto',
                    mrp: parseFloat(item.mrp || 0),
                    _destroy: false,
                    availableUnits: []
                })));
            }
        } catch (error) {
            console.error('Error fetching order:', error);
        }
    };

    function createEmptyLineItem(): LineItem {
        return {
            id: Date.now() + Math.random(),
            product_id: '',
            product_unit_id: null,
            stock_batch_id: null,
            quantity: 1,
            unit_price: 0,
            discount_percent: 0,
            tax_rate_id: '',
            tax_rate: 0,
            cgst_rate: 0,
            sgst_rate: 0,
            igst_rate: 0,
            tax_type: 'auto',
            mrp: 0,
            _destroy: false,
            availableUnits: []
        };
    }

    const searchCustomers = async (query: string) => {
        if (query.length < 2) {
            setCustomerResults([]);
            setShowCustomerResults(false);
            return;
        }

        try {
            const response = await api.get(`/search/customers?q=${encodeURIComponent(query)}`);
            setCustomerResults(response.data.customers || []);
            setShowCustomerResults(true);
        } catch (error) {
            console.error('Error searching customers:', error);
        }
    };

    const selectCustomer = (cust: any) => {
        setCustomer(cust);
        setCustomerSearch(cust.name);
        setFormData(prev => ({ ...prev, customer_id: cust.id }));
        setShowCustomerResults(false);
    };

    const clearCustomer = () => {
        setCustomer(null);
        setCustomerSearch('');
        setFormData(prev => ({ ...prev, customer_id: '' }));
    };

    const handleNewCustomerClick = () => {
        setShowNewCustomerForm(true);
        setShowCustomerResults(false);
    };

    const addLineItem = () => {
        setLineItems(prev => [...prev, createEmptyLineItem()]);
    };

    const removeLineItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const addPayment = () => {
        setPayments(prev => [...prev, { id: `new-${Date.now()}`, amount: 0, payment_method: 'cash', transaction_id: '', cheque_number: '', _destroy: false }]);
    };

    const removePayment = (index: number) => {
        setPayments(prev => prev.map((p, i) => i === index ? { ...p, _destroy: true } : p));
    };

    const updatePayment = (index: number, field: string, value: any) => {
        setPayments(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };

    const handlePaymentModeChange = (mode: string) => {
        setPaymentMode(mode);
        if (mode === 'split') {
            if (payments.filter(p => !p._destroy).length === 0) {
                addPayment();
            }
        } else {
            const firstActive = payments.find(p => !p._destroy);
            const remainingAmount = calculateTotals().total;
            setPayments([{
                amount: firstActive?.amount || remainingAmount,
                payment_method: mode,
                transaction_id: firstActive?.transaction_id || '',
                cheque_number: firstActive?.cheque_number || '',
                _destroy: false,
                id: (typeof firstActive?.id === 'string' && firstActive?.id.includes('new-')) ? undefined : firstActive?.id
            }]);
        }
    };

    const updateLineItem = (index: number, field: string, value: any) => {
        setLineItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const handleProductChange = async (index: number, productId: string) => {
        const product = products.find(p => p && String(p.id) === String(productId));
        if (product) {
            // Auto-pick the first in-stock batch for any product that has batches
            const activeBatches = (product.batches || []).filter((b: any) => (b.remaining_quantity ?? b.stock_quantity) > 0);
            const firstBatch = activeBatches[0] || null;

            setLineItems(prev => prev.map((item, i) => i === index ? {
                ...item,
                product_id: productId,
                unit_price: parseFloat(product.mrp || product.selling_price || 0),
                mrp: parseFloat(product.mrp || 0),
                tax_rate_id: firstBatch ? firstBatch.tax_rate_id : product.tax_rate_id,
                tax_rate: firstBatch ? parseFloat(firstBatch.tax_rate) || 0 : parseFloat(product.tax_rate || 0),
                stock_batch_id: firstBatch ? firstBatch.id : null,
                availableUnits: []
            } : item));

            if (product.serialized) {
                try {
                    const response = await api.get(`/products/${productId}/units/available`);
                    const units = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.units || []);
                    updateLineItem(index, 'availableUnits', units);
                } catch (error) {
                    console.error('Error fetching units:', error);
                    updateLineItem(index, 'availableUnits', []);
                }
            }
        }
    };

    // Unused calculateLineTotal removed

    const getLineItemTotals = (item: LineItem) => {
        const subtotal = item.quantity * item.unit_price;
        const discountAmount = subtotal * (item.discount_percent / 100);
        const taxableAmount = subtotal - discountAmount;
        
        let taxAmount = 0;
        let cgst = 0;
        let sgst = 0;
        let igst = 0;

        if (item.tax_type === 'igst') {
            taxAmount = taxableAmount * (item.tax_rate / 100);
            igst = taxAmount;
        } else if (item.tax_type === 'cgst_sgst') {
            const cgstRate = item.cgst_rate !== undefined ? item.cgst_rate : (item.tax_rate / 2);
            const sgstRate = item.sgst_rate !== undefined ? item.sgst_rate : (item.tax_rate / 2);
            cgst = taxableAmount * (cgstRate / 100);
            sgst = taxableAmount * (sgstRate / 100);
            taxAmount = cgst + sgst;
        } else {
            // Auto mode
            // @ts-ignore
            const firmState = (window as any).CURRENT_FIRM?.state || '';
            const clientState = customer?.state || '';
            const isInterState = firmState && clientState && firmState.toLowerCase() !== clientState.toLowerCase();
            
            taxAmount = taxableAmount * (item.tax_rate / 100);
            if (isInterState) {
                igst = taxAmount;
            } else {
                cgst = taxAmount / 2;
                sgst = taxAmount / 2;
            }
        }

        return { taxAmount, cgst, sgst, igst, total: taxableAmount + taxAmount };
    };

    const calculateTotals = () => {
        const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        const lineDiscounts = lineItems.reduce((sum, item) => {
            const itemSubtotal = item.quantity * item.unit_price;
            return sum + (itemSubtotal * (item.discount_percent / 100));
        }, 0);

        let taxAmount = 0;
        let totalCgst = 0;
        let totalSgst = 0;
        let totalIgst = 0;

        lineItems.forEach(item => {
            if (item._destroy) return;
            const res = getLineItemTotals(item);
            taxAmount += res.taxAmount;
            totalCgst += res.cgst;
            totalSgst += res.sgst;
            totalIgst += res.igst;
        });

        const total = subtotal - lineDiscounts + taxAmount - parseFloat(formData.discount_amount.toString() || '0');

        // @ts-ignore
        const firmState = (window as any).CURRENT_FIRM?.state || '';
        const clientState = customer?.state || '';
        const isInterState = firmState && clientState && firmState.toLowerCase() !== clientState.toLowerCase();

        return { subtotal, lineDiscounts, taxAmount, totalCgst, totalSgst, totalIgst, total, isInterState };
    };

    const { subtotal, lineDiscounts, taxAmount, totalCgst, totalSgst, totalIgst, total, isInterState } = calculateTotals();

    useEffect(() => {
        if (paymentMode !== 'split' && payments.length === 1) {
            setPayments(prev => {
                if (prev[0].amount !== total) {
                    return [{ ...prev[0], amount: total }];
                }
                return prev;
            });
        }
    }, [total, paymentMode]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.customer_id) {
            setErrors(['Customer selection is mandatory.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const validLineItems = lineItems.filter(item => item.product_id && !item._destroy);
        if (validLineItems.length === 0) {
            setErrors(['Please add at least one valid product.']);
            return;
        }

        const invalidStockItem = validLineItems.find(item => Number(item.quantity) <= 0);
        if (invalidStockItem) {
            setErrors(['Quantities must be greater than zero.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const exceedingStockItem = validLineItems.find(item => {
            const product = products.find(p => p && String(p.id) === String(item.product_id));
            if (product && !product.serialized && Number(item.quantity) > Number(product.stock_quantity || 0)) {
                return true;
            }
            return false;
        });

        if (exceedingStockItem) {
            setErrors(['Cannot sell more quantity than available in stock for non-serialized products.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const missingSerialUnit = validLineItems.find(item => {
            const product = products.find(p => p && String(p.id) === String(item.product_id));
            if (product && product.serialized && (!item.product_unit_id || item.product_unit_id === '')) {
                return true;
            }
            return false;
        });

        if (missingSerialUnit) {
            setErrors(['You must select a specific serial unit for all serialized products before submitting an order.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const orderData = {
            ...formData,
            order_items_attributes: lineItems.map(item => ({
                id: (typeof item.id === 'string' && (item.id as string).includes('-')) ? item.id : undefined,
                product_id: item.product_id,
                product_unit_id: item.product_unit_id,
                stock_batch_id: item.stock_batch_id || undefined,
                quantity: item.quantity,
                unit_price: item.unit_price,
                mrp: item.mrp,
                discount_percent: item.discount_percent,
                tax_rate_id: item.tax_rate_id,
                tax_rate: item.tax_rate,
                cgst_rate: item.cgst_rate,
                sgst_rate: item.sgst_rate,
                igst_rate: item.igst_rate,
                tax_type: item.tax_type,
                _destroy: item._destroy
            })),
            payments_attributes: payments.filter(p => !p._destroy || p.id).map(p => ({
                id: (typeof p.id === 'string' && (p.id as string).includes('-') || !p.id) ? undefined : p.id,
                amount: p.amount || 0,
                payment_method: p.payment_method,
                transaction_id: p.transaction_id,
                cheque_number: p.cheque_number,
                _destroy: p._destroy
            }))
        };

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await api.put(`/orders/${id}`, { order: orderData });
            } else {
                response = await api.post('/orders', { order: orderData });
            }
                
            // Check if backend reported a failure
            if (response?.success === false) {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || `Failed to ${isEdit ? 'update' : 'create'} order`];
                setErrors(backendErrors);
                
                // Show the first error in a toast for immediate feedback
                toast.error(backendErrors[0]);
                
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            
            if (isEdit) {
                toast.success('Order updated successfully');
                navigate(`/orders/${id}`);
                return;
            }
            
            const responseData = response.data || response;
            const orderId = responseData?.id;

                if (!orderId) {
                    throw new Error("Server did not return a valid order. Please try again.");
                }

                // Prepare order data for quick invoice modal
                setCreatedOrder({
                    id: orderId,
                    order_number: responseData.order_number || `ORD-${orderId}`,
                    customer_id: formData.customer_id,
                    customer_name: customer?.name || 'Customer',
                    status: formData.status || 'pending',
                    order_type: formData.order_type || 'online',
                    items: validLineItems.map(item => {
                        const product = products.find(p => p && String(p.id) === String(item.product_id));
                        return {
                            product_id: item.product_id,
                            product_name: product?.name || 'Product',
                            product_unit_id: item.product_unit_id,
                            quantity: item.quantity,
                            unit_price: item.unit_price,
                            tax_rate: item.tax_rate,
                            discount_percent: item.discount_percent
                        };
                    }),
                    subtotal: subtotal,
                    tax_amount: taxAmount,
                    total_cgst: totalCgst,
                    total_sgst: totalSgst,
                    total_igst: totalIgst,
                    is_inter_state: isInterState,
                    discount_amount: parseFloat(formData.discount_amount.toString() || '0'),
                    total: total
                });

                setShowQuickInvoice(true);
        } catch (error: any) {
            console.error('Error saving order:', error);
            setErrors(error.response?.data?.errors || [error.message || 'Problem saving order.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';

            if (e.key === 'F2') {
                e.preventDefault();
                const firstProductInput = document.querySelector('.product-select input') as HTMLElement || document.querySelector('input[placeholder*="Find by name"]') as HTMLElement;
                if (firstProductInput) firstProductInput.focus();
            } else if (e.key === 'F4') {
                e.preventDefault();
                const statusSelect = document.querySelector('select[name="status"], select[value="pending"]') as HTMLElement;
                if (statusSelect) statusSelect.focus();
            } else if (e.key === 'F9') {
                e.preventDefault();
                const submitBtn = document.querySelector('button[type="submit"]') as HTMLButtonElement;
                if (submitBtn) submitBtn.click();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                navigate('/orders');
            } else if (e.key === 'Enter') {
                if (!isInput) {
                    e.preventDefault();
                    addLineItem();
                } else if (target.tagName !== 'TEXTAREA') {
                    e.preventDefault();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);

    if (initialLoading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium">Preparing order workspace...</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Harmonized */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/orders" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            {isEdit ? 'Edit' : 'Draft'} <span className="text-indigo-600">Order</span>
                        </h1>
                    </div>
                    <p className="text-sm text-slate-500 font-medium italic ml-10">Reserve stock and track client commitments</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={loading}
                        onClick={(e) => {
                            const form = (e.target as HTMLElement).closest('form');
                            if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm ${loading ? "bg-indigo-300 text-white cursor-not-allowed" : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-100"}`}
                    >
                        {loading ? 'Processing...' : (isEdit ? 'Update Draft' : 'Commit Order')}
                        {!loading && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>}
                    </button>
                    <Link
                        to="/orders"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-600 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-all shadow-sm"
                    >
                        Discard
                    </Link>
                </div>
            </div>

            {errors.length > 0 && (
                <div className="bg-rose-50 border-l-4 border-rose-500 rounded-2xl p-4 text-rose-800 text-sm mb-8 shadow-sm flex items-start gap-3">
                    <svg className="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <ul className="list-disc list-inside font-medium italic">
                        {errors.map((error, i) => <li key={i}>{error}</li>)}
                    </ul>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 pb-32">
                {/* Step 1: Client & Schedule */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-3">
                            <span className="w-7 h-7 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-indigo-100">1</span>
                            Client & Schedule
                        </h2>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Customer Search */}
                            <div className="space-y-4">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Target Customer</label>
                                <div className="relative">
                                    {!customer ? (
                                        <div className="relative group">
                                            <input
                                                type="text"
                                                placeholder="Find by name or email..."
                                                autoComplete="off"
                                                className="w-full px-5 py-3 pl-12 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-900 placeholder:text-slate-300"
                                                value={customerSearch}
                                                onChange={(e) => {
                                                    setCustomerSearch(e.target.value);
                                                    searchCustomers(e.target.value);
                                                }}
                                            />
                                            <svg className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>

                                            {showCustomerResults && (
                                                <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl max-h-60 overflow-y-auto p-2 scrollbar-hide">
                                                    {customerResults.length > 0 ? (
                                                        customerResults.map(cust => (
                                                            <button
                                                                key={cust.id}
                                                                type="button"
                                                                onClick={() => selectCustomer(cust)}
                                                                className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-xl flex items-center justify-between group transition-colors"
                                                            >
                                                                <div>
                                                                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{cust.name}</div>
                                                                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{cust.phone || cust.email || 'Retail Partner'}</div>
                                                                </div>
                                                                <svg className="w-4 h-4 text-slate-200 group-hover:text-indigo-400 transition-all opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </button>
                                                        ))
                                                    ) : (
                                                        <div className="p-6 text-center">
                                                            <p className="text-slate-400 text-xs font-medium mb-4 italic">Customer not in database</p>
                                                            <button
                                                                type="button"
                                                                onClick={handleNewCustomerClick}
                                                                className="w-full py-3 bg-slate-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-black transition-all shadow-sm active:scale-95"
                                                            >
                                                                + Quick Register "{customerSearch}"
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-4 bg-indigo-50/40 border border-indigo-100 rounded-xl animate-in zoom-in-95 duration-200">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-white border border-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold shadow-sm ring-1 ring-indigo-50/50">
                                                    {customer.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-900 text-sm leading-tight">{customer.name}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{customer.phone || customer.email || 'Retail Partner'}</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={clearCustomer}
                                                className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all border border-transparent"
                                            >
                                                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {showNewCustomerForm && (
                                    <div className="animate-in slide-in-from-top-2 duration-300">
                                        <QuickRegistration
                                            initialName={customerSearch}
                                            onSuccess={(cust) => {
                                                selectCustomer(cust);
                                                setShowNewCustomerForm(false);
                                            }}
                                            onCancel={() => setShowNewCustomerForm(false)}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Order Details */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Placement Date</label>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={formData.order_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                                            className="w-full px-5 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-slate-900 shadow-sm"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Fulfilment Architecture</label>
                                    <div className="flex bg-slate-100/50 p-1.5 rounded-xl border border-slate-200/50 shadow-inner">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, order_type: 'online' }))}
                                            className={`flex-1 py-2 px-3 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${formData.order_type === 'online' ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-500'}`}
                                        >
                                            🌐 Online
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, order_type: 'store_sale' }))
                                                if (formData.status === 'processing' || formData.status === 'shipped') {
                                                    setFormData(prev => ({ ...prev, status: 'confirmed' }));
                                                }
                                            }}
                                            className={`flex-1 py-2 px-3 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${formData.order_type === 'store_sale' ? 'bg-white text-emerald-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-500'}`}
                                        >
                                            🏪 Retail
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 2: Line Items */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mt-8 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-75">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-3">
                            <span className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-emerald-100">2</span>
                            Ordered Products
                        </h2>
                        <button
                            type="button"
                            onClick={addLineItem}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-sm active:scale-95"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                            Select Product
                        </button>
                    </div>

                    <div className="p-0">
                        <div className="divide-y divide-slate-100">
                            {lineItems.filter(item => !item._destroy).map((item, index) => (
                                <div key={item.id} className="p-6 hover:bg-slate-50/50 transition-colors group relative">
                                {/* Decorative Gradient Blobs with isolated overflow */}
                                
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start relative z-10">
                                    <div className="lg:col-span-3 space-y-3">
                                        <ProductSelect
                                            value={item.product_id}
                                            products={products}
                                            onChange={(productId) => handleProductChange(index, String(productId))}
                                        />

                                        {(() => {
                                            const product = products.find(p => p && String(p.id) === String(item.product_id));
                                            if (product?.serialized) {
                                                return (
                                                    <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                                        <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest pl-1 mb-1 block">
                                                            Select Serialized Unit <span className="text-rose-500">*</span>
                                                        </label>
                                                        {item.availableUnits && item.availableUnits.length > 0 ? (
                                                            <>
                                                                <select
                                                                    value={item.product_unit_id || ''}
                                                                    onChange={(e) => updateLineItem(index, 'product_unit_id', e.target.value)}
                                                                    className="w-full px-4 py-3 bg-indigo-50/50 border border-indigo-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm font-bold text-indigo-900"
                                                                >
                                                                    <option value="">-- Select Unit --</option>
                                                                    {item.availableUnits.map((unit) => unit && unit.id ? (
                                                                        <option key={unit.id} value={unit.id}>
                                                                            {unit.primary_identifier || unit.serial_number || unit.batch_number || unit.id}
                                                                            {unit.location ? ` - ${unit.location.name}` : ''}
                                                                        </option>
                                                                    ) : null)}
                                                                </select>
                                                                {!item.product_unit_id && (
                                                                    <p className="text-[10px] text-rose-500 font-bold mt-1 pl-1">⚠ Required for this product</p>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className="px-4 py-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs font-bold flex items-center gap-2">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <span>No stock units available for this item.</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            // Show batch picker for any product with stock batches
                                            if (product?.batches?.length > 0) {
                                                const activeBatches = (product.batches || []).filter((b: any) => (b.remaining_quantity ?? b.stock_quantity) > 0);
                                                return (
                                                    <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                                        <label className="text-[10px] font-black text-amber-500 uppercase tracking-widest pl-1 mb-1 block">
                                                            📦 Select Stock Batch
                                                        </label>
                                                        {activeBatches.length > 0 ? (
                                                            <select
                                                                value={item.stock_batch_id || ''}
                                                                onChange={(e) => {
                                                                    const batchId = e.target.value;
                                                                    const batch = product.batches.find((b: any) => String(b.id) === String(batchId));
                                                                    updateLineItem(index, 'stock_batch_id', batchId);
                                                                    if (batch) {
                                                                        updateLineItem(index, 'tax_rate_id', batch.tax_rate_id);
                                                                        updateLineItem(index, 'tax_rate', parseFloat(batch.tax_rate) || 0);
                                                                        updateLineItem(index, 'unit_price', parseFloat(batch.mrp || product.mrp || product.selling_price || 0));
                                                                        // Buy price will auto-reflect via computed value below
                                                                    }
                                                                }}
                                                                className="w-full px-3 py-2.5 bg-amber-50/50 border border-amber-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-400/20 focus:border-amber-400 transition-all text-xs font-bold text-amber-900"
                                                            >
                                                                <option value="">-- Select Batch --</option>
                                                                {activeBatches.map((b: any) => (
                                                                    <option key={b.id} value={b.id}>
                                                                        {b.batch_number} │ Stock: {b.remaining_quantity ?? b.stock_quantity} │ MRP: ₹{parseFloat(b.mrp ?? 0).toFixed(2)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <div className="px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl text-rose-500 text-xs font-bold">
                                                                No in-stock batches available.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>

                                    <div className="lg:col-span-6 grid grid-cols-1 md:grid-cols-5 lg:grid-cols-6 gap-4">
                                        {/* Qty & Pricing Group */}
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] text-center block">Quantity</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-2 py-3 bg-white border border-slate-200 rounded-xl outline-none font-black text-slate-900 shadow-sm text-center focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block">Buy Price</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 font-bold text-[10px]">₹</span>
                                                <input
                                                    type="number"
                                                    value={(() => {
                                                        const prod = products.find((p: any) => p && String(p.id) === String(item.product_id));
                                                        if (item.stock_batch_id && prod?.batches) {
                                                            const b = prod.batches.find((b: any) => String(b.id) === String(item.stock_batch_id));
                                                            if (b) return parseFloat(b.unit_cost ?? b.mrp ?? 0).toFixed(2);
                                                        }
                                                        return parseFloat((prod?.purchase_price || item.unit_price || 0) as any).toFixed(2);
                                                    })()}
                                                    className="w-full pl-6 pr-2 py-3 bg-slate-50 border border-slate-100 rounded-xl outline-none font-black text-slate-400 shadow-inner text-sm"
                                                    readOnly
                                                    tabIndex={-1}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block">MRP</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 font-bold text-[10px]">₹</span>
                                                <input
                                                    type="number"
                                                    value={(() => {
                                                        const prod = products.find((p: any) => p && String(p.id) === String(item.product_id));
                                                        if (item.stock_batch_id && prod?.batches) {
                                                            const b = prod.batches.find((b: any) => String(b.id) === String(item.stock_batch_id));
                                                            if (b) return parseFloat(b.mrp ?? 0).toFixed(2);
                                                        }
                                                        return parseFloat((item.mrp || prod?.mrp || 0) as any).toFixed(2);
                                                    })()}
                                                    className="w-full pl-6 pr-2 py-3 bg-emerald-50/30 border border-emerald-100 rounded-xl outline-none font-black text-emerald-600/60 shadow-inner text-sm"
                                                    readOnly
                                                    tabIndex={-1}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block">Selling Price</label>
                                            <div className="relative group">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 font-bold text-[10px] group-focus-within:text-indigo-500 transition-colors">₹</span>
                                                <input
                                                    type="number"
                                                    value={item.unit_price}
                                                    onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                                    className="w-full pl-6 pr-2 py-3 bg-white border border-slate-200 rounded-xl outline-none font-black text-slate-800 shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block text-center">Discount %</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={item.discount_percent}
                                                    onChange={(e) => {
                                                        let val = parseFloat(e.target.value) || 0;
                                                        if (val > 100) val = 100;
                                                        if (val < 0) val = 0;
                                                        updateLineItem(index, 'discount_percent', val);
                                                    }}
                                                    className="w-full px-2 py-3 bg-indigo-50/30 border border-indigo-100 rounded-xl outline-none font-black text-indigo-700 shadow-sm text-center focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                                                    placeholder="0"
                                                    min="0"
                                                    max="100"
                                                />
                                            </div>
                                        </div>

                                        {/* Tax Intel Section - Spanning full width on mobile, last col on LG */}
                                        <div className="col-span-full lg:col-span-1 space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block text-center">Tax Intel</label>
                                            <div className="flex flex-col gap-2">
                                                <div className="relative group">
                                                    <select
                                                        value={item.tax_rate_id || ''}
                                                        onChange={(e) => {
                                                            const rateId = e.target.value;
                                                            const rate = taxRates.find(r => String(r.id) === String(rateId));
                                                            updateLineItem(index, 'tax_rate_id', rateId);
                                                            if (rate) {
                                                                updateLineItem(index, 'tax_rate', parseFloat(rate.percentage));
                                                            }
                                                        }}
                                                        className="w-full pl-3 pr-8 py-2.5 bg-emerald-50/50 border border-emerald-100 rounded-xl outline-none font-black text-emerald-700 shadow-sm text-sm appearance-none cursor-pointer focus:border-emerald-500 transition-all"
                                                    >
                                                        <option value="">Select Tax</option>
                                                        {(() => {
                                                            const product = products.find((p: any) => p && String(p.id) === String(item.product_id));
                                                            return (
                                                                <>
                                                                    <optgroup label="🏢 Tax Registry">
                                                                        {taxRates.map(tr => (
                                                                            <option key={tr.id} value={tr.id}>{tr.name} ({tr.percentage}%)</option>
                                                                        ))}
                                                                    </optgroup>
                                                                    {product?.batches?.length > 0 && (
                                                                        <optgroup label="📦 Stock Batches">
                                                                            {product.batches.map((b: any) => (
                                                                                <option key={`batch-${b.id}`} value={b.tax_rate_id || ''}>
                                                                                    {b.tax_rate}% (Batch: {b.batch_number})
                                                                                </option>
                                                                            ))}
                                                                        </optgroup>
                                                                    )}
                                                                    {product && (
                                                                        <optgroup label="✨ Product Default">
                                                                            <option value={product.tax_rate_id || ''}>
                                                                                {product.tax_rate}% Default
                                                                            </option>
                                                                        </optgroup>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </select>
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-emerald-400">
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                                                    </div>
                                                </div>

                                                <TaxIntel 
                                                    taxType={item.tax_type}
                                                    cgstRate={item.cgst_rate || 0}
                                                    sgstRate={item.sgst_rate || 0}
                                                    igstRate={item.igst_rate || 0}
                                                    onChange={(field, value) => updateLineItem(index, field, value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="lg:col-span-3 flex items-start justify-between lg:justify-end gap-4 pt-1 lg:pt-0 border-t lg:border-t-0 border-slate-100 h-full">
                                        <div className="text-right flex flex-col justify-end h-full mt-auto mb-1">
                                            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-0.5">+GST: ₹{getLineItemTotals(item).taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                            <p className="text-xl font-black text-slate-900 tracking-tighter">₹{getLineItemTotals(item).total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLineItem(index)}
                                            className="w-12 h-12 mt-auto mb-1 bg-white text-rose-500 rounded-2xl flex items-center justify-center hover:bg-rose-100 transition-all border border-slate-100 shrink-0"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Step 3: Order Finalization & Valuation */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-8 space-y-8">
                        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/30 flex items-center gap-3">
                                <span className="w-7 h-7 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-amber-100">3</span>
                                <h2 className="text-sm font-bold text-slate-800">Order Finalization</h2>
                            </div>
                            
                            <div className="p-8 space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Lifecycle Status</label>
                                        <div className="relative group">
                                            <select
                                                value={formData.status}
                                                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 appearance-none cursor-pointer shadow-sm group-hover:border-slate-300"
                                            >
                                                <option value="pending">⏳ Pending Confirmation</option>
                                                <option value="confirmed">✅ Confirmed</option>
                                                {formData.order_type !== 'store_sale' && (
                                                    <>
                                                        <option value="processing">⚙️ Processing</option>
                                                        <option value="shipped">🚚 Shipped</option>
                                                    </>
                                                )}
                                                <option value="delivered">📦 Delivered</option>
                                                <option value="cancelled">🚫 Cancelled</option>
                                            </select>
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Payment Protocol</label>
                                        <div className="flex bg-slate-100/50 p-1.5 rounded-xl border border-slate-200/50 shadow-inner">
                                            {['cash', 'upi', 'debit_card', 'credit_card', 'bank_transfer', 'split'].map((mode) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    onClick={() => handlePaymentModeChange(mode)}
                                                    className={`flex-1 py-2 px-1 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all ${paymentMode === mode ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-500'}`}
                                                >
                                                    {mode.split('_')[0]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-4 duration-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Settlement Intelligence</h3>
                                        {paymentMode === 'split' && (
                                            <button type="button" onClick={addPayment} className="text-[9px] font-black uppercase text-indigo-600 tracking-widest hover:text-indigo-800 transition-colors">+ Add Layer</button>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {payments.map((payment, pIndex) => !payment._destroy && (
                                            <div key={payment.id || pIndex} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 relative group animate-in slide-in-from-top-2 duration-300">
                                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                                    <div className="md:col-span-3 space-y-2">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-tight ml-1">Volume (₹)</label>
                                                        <div className="relative">
                                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">₹</span>
                                                            <input
                                                                type="number"
                                                                placeholder="0.00"
                                                                value={payment.amount}
                                                                onChange={(e) => updatePayment(pIndex, 'amount', e.target.value)}
                                                                className="w-full pl-7 pr-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/10 font-black text-slate-900 transition-all text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="md:col-span-3 space-y-2">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-tight ml-1">Method</label>
                                                        {paymentMode === 'split' ? (
                                                            <select
                                                                value={payment.payment_method}
                                                                onChange={(e) => updatePayment(pIndex, 'payment_method', e.target.value)}
                                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all font-black text-slate-900 text-xs appearance-none cursor-pointer"
                                                            >
                                                                <option value="cash">💵 Cash</option>
                                                                <option value="upi">📲 UPI</option>
                                                                <option value="debit_card">💳 Debit</option>
                                                                <option value="credit_card">💳 Credit</option>
                                                                <option value="bank_transfer">🏦 Bank</option>
                                                            </select>
                                                        ) : (
                                                            <div className="w-full px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-[10px] font-black text-indigo-600 flex items-center justify-center uppercase tracking-tighter">
                                                                {paymentMode.replace('_', ' ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="md:col-span-5 space-y-2">
                                                        {(['upi', 'bank_transfer', 'bank_deposit', 'credit_card', 'debit_card'].includes(paymentMode === 'split' ? payment.payment_method : paymentMode)) && (
                                                            <>
                                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-tight ml-1">Reference Protocol</label>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Txn ID / Ref #"
                                                                    value={payment.transaction_id}
                                                                    onChange={(e) => updatePayment(pIndex, 'transaction_id', e.target.value)}
                                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/10 transition-all font-black text-slate-900 text-xs shadow-inner"
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="md:col-span-1 flex items-center justify-end">
                                                        {paymentMode === 'split' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removePayment(pIndex)}
                                                                className="w-9 h-9 bg-white text-rose-500 rounded-lg flex items-center justify-center hover:bg-rose-50 hover:text-rose-600 transition-colors border border-slate-200 shadow-sm"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4 pt-4 border-t border-slate-100">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Internal Staff Observations</label>
                                    <textarea
                                        rows={4}
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black resize-none text-slate-900 shadow-inner placeholder:text-slate-300 text-sm"
                                        placeholder="Add fulfillment details, special instructions, or warehouse notes..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4 space-y-6 sticky top-8">
                        <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden p-8 text-white relative">
                            {/* Decorative Blobs */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-3xl animate-pulse"></div>
                            
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                                Valuation Intelligence
                            </h3>
                            
                            <div className="space-y-4 relative z-10">
                                <div className="flex justify-between items-center text-sm font-medium text-slate-400 uppercase tracking-wider">
                                    <span>Gross Value</span>
                                    <span className="font-bold text-slate-200">₹{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm font-medium text-rose-400 uppercase tracking-wider">
                                    <span>Product Rewards</span>
                                    <span className="font-bold">-₹{lineDiscounts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm font-medium text-emerald-400/80 uppercase tracking-wider">
                                    <span>GST Architecture</span>
                                    <span className="font-bold">+₹{taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                
                                <div className="pt-4 border-t border-white/5 space-y-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Adjustment Protocol (₹)</label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400 font-bold text-sm">₹</span>
                                            <input
                                                type="number"
                                                value={formData.discount_amount}
                                                onChange={(e) => setFormData(prev => ({ ...prev, discount_amount: parseFloat(e.target.value) || 0 }))}
                                                className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-right font-black text-white outline-none focus:border-indigo-500 transition-all text-xl"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="pt-8 mt-4 border-t border-white/10">
                                    <div className="flex justify-between items-center text-xs font-black text-indigo-400 uppercase tracking-[0.2em] mb-4">
                                        <span>Total Valuation</span>
                                        <span>₹{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-white/10 pb-3 mb-4">
                                        <span>Receipt Volume</span>
                                        <span className="text-white">₹{payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between items-end">
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Balance Protocol</span>
                                        <span className={`text-4xl font-black tracking-tighter ${Math.max(0, total - payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0)) > 0 ? 'text-rose-500 drop-shadow-[0_0_10px_rgba(244,63,94,0.4)]' : 'text-emerald-500'}`}>
                                            ₹{Math.max(0, total - payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-indigo-600 rounded-3xl p-1 shadow-2xl shadow-indigo-200/50 group">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-[22px] font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        {isEdit ? 'Update Draft' : 'Commit Order'}
                                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/* Quick Invoice Modal */}
            <QuickInvoiceModal
                isOpen={showQuickInvoice}
                onClose={() => {
                    setShowQuickInvoice(false);
                    // Navigate to order show page after closing modal
                    if (createdOrder) {
                        navigate(`/orders/${createdOrder.id}`);
                    }
                }}
                order={createdOrder}
            />

            {/* Keyboard Shortcuts Guide */}
            <div className="fixed bottom-6 right-6 z-50 group">
                <button type="button" className="w-12 h-12 bg-indigo-600 text-white rounded-full shadow-2xl flex items-center justify-center font-black text-xl hover:scale-110 transition-transform">
                    ⌨️
                </button>
                <div className="absolute bottom-16 right-0 w-64 bg-slate-900 border border-slate-700 text-slate-300 p-4 rounded-2xl shadow-2xl opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all pointer-events-none text-xs font-mono">
                    <h4 className="text-white font-bold mb-3 uppercase tracking-widest border-b border-slate-700 pb-2">POS Shortcuts</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between"><span>[Enter]</span><span className="text-indigo-400">Add Line Item</span></div>
                        <div className="flex justify-between"><span>[F2]</span><span className="text-indigo-400">Product Search</span></div>
                        <div className="flex justify-between"><span>[F4]</span><span className="text-indigo-400">Focus Status</span></div>
                        <div className="flex justify-between"><span>[F9]</span><span className="text-emerald-400">Submit Registry</span></div>
                        <div className="flex justify-between"><span>[Esc]</span><span className="text-rose-400">Cancel Return</span></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderForm;
